import React, { useEffect } from "react";
import { AdminClaimSummarySection } from "./components/AdminClaimSummarySection";
import { AdminHijackUserButton } from "kw/admin/components/AdminHijackUserButton";
import { AdminNotesSection } from "./components/AdminNotesSection";
import { AdminRiskStateSection } from "./components/AdminRiskStateSection";
import { HeadingActionsBar } from "kanopi-components/HeadingActionBar/HeadingActionsBar";
import { KIND, SIZE } from "kanopi-components/Button";
import { Placeholder } from "kanopi-components/Placeholder";
import { SummaryCard } from "./components/SummaryCard";
import { useClaims } from "./hooks/useClaims";
import { useGlobal } from "kanopi-components/useGlobal";
import { VerticalSpacer } from "kanopi-components/SpaceWrapper/VerticalSpacer";
import { COLOR } from "baseui/badge";
import { ClaimStatusTypes, ClaimStatusChoices } from "kw/api/zeus";
import { AdminCloseClaimButton } from "kw/admin/components/AdminCloseClaimModal";
import { useAPI } from "kw/api/useAPI";
import { toaster } from "kanopi-components/ToastContainer";
import { NoteAssociationLimit } from "kw/api/zeus";

export const ClaimDetailsPage = ({ claimId }: { claimId: string }) => {
  const { claim, notes, loading, fetchClaim } = useClaims();
  const [, setHijack] = useGlobal("hijack");

  useEffect(() => {
    if (claimId) {
      fetchClaim(claimId);
      setHijack({
        nextUrl: `/claims/${claimId}`,
        releaseToUrl: `/admin/claims/${claimId}`,
      });
    }
  }, [claimId]);

  const headingCaptionProps = (claim: TClaim) => {
    let color;
    switch (claim?.statusType) {
      case ClaimStatusTypes.OPEN:
        color = COLOR.positive;
        break;
      case ClaimStatusTypes.CLOSED:
        color = COLOR.negative;
        break;
    }
    return { caption: claim?.statusTypeDisplay, captionColor: color };
  };

  const { mutation } = useAPI();
  const closeClaim = (decision: ClaimStatusChoices.PAID) => {
    mutation(
      {
        admin: {
          closeClaim: [
            {
              decision: decision,
              claimId: claim.id,
            },
            {
              success: true,
            },
          ],
        },
      },
      (response: { admin: { closeClaim: { success: boolean } } }) => {
        // An unsuccessful response will generate an error handled by the API hook
        if (response.admin.closeClaim.success) {
          toaster({
            kind: "POSITIVE",
            content: `Claim has been ${decision.toLocaleLowerCase()}.`,
            key: "close-claim-success-toast",
          });
          fetchClaim(claim.id);
        }
      }
    );
  };

  return (
    <Placeholder loading={!claim || loading}>
      <VerticalSpacer>
        <HeadingActionsBar
          heading={`Claim # ${claim?.id}`}
          actions={[
            claim?.statusType === ClaimStatusTypes.OPEN && (
              <AdminCloseClaimButton
                claim={claim}
                closeClaim={closeClaim}
                data-testid={"closeClaimButton"}
              />
            ),
          ]}
          {...headingCaptionProps(claim)}
        />
        <SummaryCard
          data-testid="claimSummaryArea"
          title="Claim summary"
          ctas={[
            <AdminHijackUserButton
              key={0}
              userId={claim?.claimProfile?.owner?.id}
              userIsActive={claim?.claimProfile?.owner?.isActive}
              kind={KIND.secondary}
              size={SIZE.mini}
              outlined={true}
            />,
          ]}
        >
          <AdminClaimSummarySection claim={claim} />
        </SummaryCard>
        <SummaryCard data-testid="claimDetailsArea" title="Claim details">
          <AdminRiskStateSection
            riskProfile={claim?.claimProfile}
            questionSets={claim?.claimProfile?.riskStateQuestionSets}
          />
        </SummaryCard>
        <SummaryCard data-testid="claimNotesArea" title="Notes">
          <AdminNotesSection
            notes={notes}
            fetchAssocObj={fetchClaim}
            assocObjId={claim?.id}
            assocObjModel={NoteAssociationLimit.CLAIM}
          />
        </SummaryCard>
      </VerticalSpacer>
    </Placeholder>
  );
};
